/**
 * Analytics module for tracking user behavior and custom events
 * 
 * This module provides a privacy-friendly analytics implementation that can
 * work with various analytics services (Google Analytics, Plausible, etc.)
 * 
 * To use analytics:
 * 1. Set VITE_ANALYTICS_ENABLED=true in .env
 * 2. Optionally set VITE_GA_MEASUREMENT_ID for Google Analytics
 * 3. Call trackEvent() to track custom events
 */

interface AnalyticsEvent {
  category: string
  action: string
  label?: string
  value?: number
}

interface PageViewEvent {
  path: string
  title?: string
}

/**
 * Check if analytics is enabled via environment variable
 */
function isAnalyticsEnabled(): boolean {
  return import.meta.env.VITE_ANALYTICS_ENABLED === 'true'
}

/**
 * Track a page view
 */
export function trackPageView({ path, title }: PageViewEvent): void {
  if (!isAnalyticsEnabled()) return

  // Google Analytics 4
  const gaMeasurementId = import.meta.env.VITE_GA_MEASUREMENT_ID
  if (gaMeasurementId && typeof window.gtag === 'function') {
    window.gtag('config', gaMeasurementId, {
      page_path: path,
      page_title: title,
    })
  }

  // Plausible Analytics (if configured)
  if (typeof window.plausible === 'function') {
    window.plausible('pageview', { 
      url: path,
    })
  }

  console.debug('[Analytics] Page view:', path, title)
}

/**
 * Track a custom event
 */
export function trackEvent({
  category,
  action,
  label,
  value,
}: AnalyticsEvent): void {
  if (!isAnalyticsEnabled()) return

  // Google Analytics 4
  if (typeof window.gtag === 'function') {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
    })
  }

  // Plausible Analytics (custom events)
  if (typeof window.plausible === 'function') {
    window.plausible(action, {
      props: {
        category,
        label,
        value,
      },
    })
  }

  console.debug('[Analytics] Event:', { category, action, label, value })
}

/**
 * Track word submission
 */
export function trackWordSubmission(wordId: string): void {
  trackEvent({
    category: 'Word',
    action: 'submit_word',
    label: wordId,
  })
}

/**
 * Track alternative submission
 */
export function trackAlternativeSubmission(alternativeId: string): void {
  trackEvent({
    category: 'Alternative',
    action: 'submit_alternative',
    label: alternativeId,
  })
}

/**
 * Track vote
 */
export function trackVote(voteType: 'upvote' | 'downvote', alternativeId: string): void {
  trackEvent({
    category: 'Vote',
    action: voteType,
    label: alternativeId,
  })
}

/**
 * Track search
 */
export function trackSearch(query: string): void {
  trackEvent({
    category: 'Search',
    action: 'search',
    label: query,
  })
}

/**
 * Track filter usage
 */
export function trackFilter(filterType: string, filterValue: string): void {
  trackEvent({
    category: 'Filter',
    action: filterType,
    label: filterValue,
  })
}

/**
 * Track sign in
 */
export function trackSignIn(method: string): void {
  trackEvent({
    category: 'Auth',
    action: 'sign_in',
    label: method,
  })
}

/**
 * Track sign up
 */
export function trackSignUp(method: string): void {
  trackEvent({
    category: 'Auth',
    action: 'sign_up',
    label: method,
  })
}

/**
 * Track error
 */
export function trackError(errorMessage: string, errorType?: string): void {
  trackEvent({
    category: 'Error',
    action: errorType || 'error',
    label: errorMessage,
  })
}

// Extend Window interface for analytics globals
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void
    plausible?: (eventName: string, options?: { props?: Record<string, unknown>; url?: string }) => void
  }
}
